import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

function useLive(base, spread, dp = 1) {
  const [v, setV] = useState(base);
  useEffect(() => {
    const iv = setInterval(() => setV(base + (Math.random() - 0.5) * spread), 1500);
    return () => clearInterval(iv);
  }, [base, spread]);
  return v.toFixed(dp);
}

function Widget({ label, value, unit }) {
  return (
    <div className="glass-dark rounded-xl p-4" data-testid={`mfd-widget-${label}`}>
      <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-slate mb-2">{label}</p>
      <p className="font-mono text-2xl md:text-3xl text-cyan tabular-nums leading-none">
        {value}
        {unit && <span className="text-sm text-slate ml-1">{unit}</span>}
      </p>
    </div>
  );
}

export default function Chartplotter() {
  const { t } = useApp();
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  const speed = useLive(12.6, 3);
  const heading = useLive(46, 8, 0);
  const depth = useLive(18.2, 5);
  const w = t.tech.widgets;

  return (
    <section id="tech" className="relative py-28 lg:py-40 bg-navy text-white overflow-hidden" data-testid="tech-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">{t.tech.title}</h2>
          <p className="mt-6 max-w-lg text-slate leading-relaxed">{t.tech.sub}</p>
          <button onClick={() => go("#contact")} data-testid="tech-cta"
            className="mt-9 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider border border-cyan text-cyan px-6 py-4 rounded-sm hover:bg-cyan hover:text-white transition-all duration-300">
            {t.tech.cta} <ArrowUpRight className="w-4 h-4" />
          </button>

          <div className="mt-10 grid grid-cols-4 gap-3 max-w-lg">
            <Widget label={w.speed} value={speed} unit="kn" />
            <Widget label={w.heading} value={heading} unit="°" />
            <Widget label={w.depth} value={depth} unit="m" />
            <div className="glass-dark rounded-xl p-4" data-testid="mfd-widget-bus">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-slate mb-2">{w.bus}</p>
              <p className="font-mono text-sm text-cyan leading-none flex items-center gap-1.5 pt-2">
                <span className="w-2 h-2 rounded-full bg-cyan animate-pulse" />ONLINE
              </p>
            </div>
          </div>
        </Reveal>

        {/* Real bridge with chartplotter inset */}
        <Reveal delay={0.15}>
          <div className="relative aspect-[4/3]" data-testid="mfd-chart">
            {/* bridge with computers */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden ring-1 ring-cyan/25 bg-navy">
              <img src={IMAGES.bridge} alt="Yacht bridge with navigation computers" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30" />
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.28em] uppercase text-white/90">The bridge</div>
            </div>

            {/* real chartplotter photo inset (Lowrance HDS with chart visible) */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="absolute -bottom-6 -right-4 w-3/5 rounded-xl overflow-hidden shadow-2xl ring-1 ring-cyan/40"
              data-testid="mfd-chart-inset"
            >
              <div className="relative aspect-[4/3]">
                <img src={IMAGES.nauticalChart} alt="TimeZero-style chartplotter" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
                <div className="absolute bottom-2 left-2 glass-dark rounded-md px-2.5 py-1.5 flex items-center gap-2" data-testid="mfd-hud-live">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
                  <span className="font-mono text-[9px] tracking-[0.28em] uppercase text-cyan">TIMEZERO · LIVE</span>
                </div>
              </div>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
